'use client';

import { c as _c } from "react/compiler-runtime";
import { AIFI_CHART_PRIMARY_COLOR } from '@/domains/pages/ai-proficiency/constants';
import { SPEND_RANGE_MAX_TICK_COLOR, SPEND_RANGE_MAX_TICK_COLOR_LIT, SPEND_RANGE_ORG_AVERAGE_LABEL, SPEND_RANGE_ORG_AVERAGE_TOOLTIP } from '@/domains/pages/ai-spend/constants';
import { rangePositionPercent } from '@/domains/pages/ai-spend/utils/spend-range-position';
import { ChartTooltipPanel, FloatingTooltip } from '@/domains/pages/shared/chart-tooltip';
import { PowerBarMarker } from '@/domains/pages/shared/power-bar-marker';
import { POWER_BAR_MARKER_LINE_WIDTH, POWER_BAR_MEDIAN_TICK_COLOR, POWER_BAR_MEDIAN_TICK_COLOR_LIT, POWER_BAR_TRACK_CLASS, STANDARD_POWER_BAR_LAYOUT, powerBarColoredFillStyle } from '@/domains/pages/shared/power-bar-shared';
import { TooltipButton } from '@/comps/tooltip-button';
import { PAGE_DATA_NUMBER_CLASS } from '@/domains/pages/shared/typography';
import { selectedValueTone } from '@/domains/pages/shared/value-tone';
import { computeDisplaySpendDelta, formatNumber, formatSpend, isSpendAtOrBelowBenchmark } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { Info } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
type SpendRangeBarProps = {
  selectedName: string;
  selectedValue: number | null;
  cheapest: {
    name: string;
    value: number;
  } | null;
  mostExpensive: {
    name: string;
    value: number;
  } | null;
  orgAverage: number | null;
  segmentNoun: string;
  segmentNounPlural: string;
  rank: number;
  rankTotal: number;
  formatSegmentName?: (name: string) => string;
  className?: string;
};
type SpendRangeBarHoverTarget = 'bar' | 'average' | 'mostExpensive';
function segmentRangeLabel(prefix: string, segmentNoun: string): string {
  return `${prefix} ${segmentNoun}`;
}
function mostExpensiveTooltip(segmentNounPlural: string): string {
  return `Highest AI spend per task among all ${segmentNounPlural}. This sets the upper end of the range.`;
}

/** Marker swatch, label and an info icon — one row of the range-bar legend. */
function RangeLegendItem(t0) {
  const $ = _c(13);
  const {
    color,
    label,
    tooltip
  } = t0;
  let t1;
  if ($[0] !== color) {
    t1 = <span className="h-3 shrink-0 rounded-full" style={{
      width: POWER_BAR_MARKER_LINE_WIDTH,
      backgroundColor: color
    }} />;
    $[0] = color;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <span className="truncate">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Info className="size-3 shrink-0 cursor-default text-muted-foreground transition-colors hover:text-foreground" strokeWidth={2} />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== tooltip) {
    t4 = <TooltipButton content={tooltip}>{t3}</TooltipButton>;
    $[5] = tooltip;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4) {
    t5 = <span className="flex min-w-0 items-center gap-1">{t2}{t4}</span>;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t1 || $[11] !== t5) {
    t6 = <span className="flex min-w-0 items-center gap-2">{t1}{t5}</span>;
    $[10] = t1;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
function ratioFromPositionPercent(position: string): number {
  return parseFloat(position) / 100;
}
export function SpendRangeBar({
  selectedName,
  selectedValue,
  cheapest,
  mostExpensive,
  orgAverage,
  segmentNoun,
  segmentNounPlural,
  rank,
  rankTotal,
  formatSegmentName = name => name,
  className
}: SpendRangeBarProps) {
  const segmentLabel = formatSegmentName(selectedName);
  const mostExpensiveSegmentLabel = mostExpensive ? formatSegmentName(mostExpensive.name) : null;
  const barRef = useRef<HTMLDivElement>(null);
  const [hoverTarget, setHoverTarget] = useState<SpendRangeBarHoverTarget | null>(null);
  const [tooltipPos, setTooltipPos] = useState({
    x: 0,
    y: 0
  });
  const domainMin = cheapest?.value ?? mostExpensive?.value ?? 0;
  const domainMax = mostExpensive?.value ?? cheapest?.value ?? 0;
  const hasDomain = cheapest != null && mostExpensive != null;
  const isDegenerate = hasDomain && domainMin === domainMax;
  const cheapestLabel = segmentRangeLabel('Cheapest', segmentNoun);
  const mostExpensiveLabel = segmentRangeLabel('Most expensive', segmentNoun);
  const averageLabel = SPEND_RANGE_ORG_AVERAGE_LABEL;
  const mostExpensiveMarkerLabel = `${mostExpensiveLabel} marker`;
  const layout = STANDARD_POWER_BAR_LAYOUT;
  const barTopPx = layout.trackTopPx;
  const positionForValue = useCallback((value: number) => rangePositionPercent(value, domainMin, domainMax), [domainMax, domainMin]);
  const updateTooltipAtRatio = useCallback((ratio: number) => {
    const bar = barRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    setTooltipPos({
      x: rect.left + rect.width * Math.min(1, Math.max(0, ratio)),
      y: rect.top
    });
  }, []);
  const showBarTooltip = useCallback(() => {
    if (selectedValue != null && hasDomain) {
      updateTooltipAtRatio(ratioFromPositionPercent(positionForValue(selectedValue)));
    } else {
      updateTooltipAtRatio(0.5);
    }
    setHoverTarget('bar');
  }, [hasDomain, positionForValue, selectedValue, updateTooltipAtRatio]);
  const showAverageTooltip = useCallback(() => {
    if (orgAverage == null || !hasDomain) return;
    updateTooltipAtRatio(ratioFromPositionPercent(positionForValue(orgAverage)));
    setHoverTarget('average');
  }, [hasDomain, orgAverage, positionForValue, updateTooltipAtRatio]);
  const showMostExpensiveTooltip = useCallback(() => {
    if (!hasDomain) return;
    updateTooltipAtRatio(1);
    setHoverTarget('mostExpensive');
  }, [hasDomain, updateTooltipAtRatio]);
  const hideTooltip = useCallback(() => setHoverTarget(null), []);
  const barLit = hoverTarget === 'bar';
  const averageLit = hoverTarget === 'average';
  const mostExpensiveLit = hoverTarget === 'mostExpensive';
  const hasAverageComparison = orgAverage != null && selectedValue != null;
  const vsAverage = hasAverageComparison ? computeDisplaySpendDelta(selectedValue, orgAverage) : 0;
  const isAtOrBelowAverage = hasAverageComparison && isSpendAtOrBelowBenchmark(selectedValue, orgAverage);
  const spendTone = hasAverageComparison ? selectedValueTone(isAtOrBelowAverage) : {};
  const differenceRow = hasAverageComparison ? [{
    label: 'Difference',
    value: `${isAtOrBelowAverage ? '' : '+'}${formatSpend(vsAverage)}`,
    ...spendTone
  }] : [];

  // The max marker measures the selected segment against the priciest one, not against the org.
  const vsMostExpensive = selectedValue != null && mostExpensive != null ? computeDisplaySpendDelta(selectedValue, mostExpensive.value) : 0;
  const isAtOrBelowMostExpensive = selectedValue != null && mostExpensive != null && isSpendAtOrBelowBenchmark(selectedValue, mostExpensive.value);
  const isSelectedMostExpensive = mostExpensive != null && mostExpensive.name === selectedName;
  const mostExpensiveComparisonRows = mostExpensive != null && selectedValue != null && !isSelectedMostExpensive ? [{
    label: segmentLabel,
    value: formatSpend(selectedValue),
    ...selectedValueTone(isAtOrBelowMostExpensive)
  }, {
    label: 'Difference',
    value: `${isAtOrBelowMostExpensive ? '' : '+'}${formatSpend(vsMostExpensive)}`,
    ...selectedValueTone(isAtOrBelowMostExpensive)
  }] : [];
  const tooltipRows = hoverTarget === 'bar' ? [{
    label: segmentLabel,
    value: selectedValue != null ? formatSpend(selectedValue) : '—',
    valueClassName: 'text-foreground'
  }, {
    label: averageLabel,
    value: orgAverage != null ? formatSpend(orgAverage) : '—'
  }, {
    label: cheapestLabel,
    value: cheapest != null ? formatSpend(cheapest.value) : '—'
  }, {
    label: mostExpensiveLabel,
    value: mostExpensive != null ? formatSpend(mostExpensive.value) : '—'
  }, ...(rank > 0 && rankTotal > 0 ? [{
    label: 'Rank',
    value: `#${rank} of ${formatNumber(rankTotal)}`
  }] : [])] : hoverTarget === 'average' ? [{
    label: averageLabel,
    value: orgAverage != null ? formatSpend(orgAverage) : '—'
  }, {
    label: segmentLabel,
    value: selectedValue != null ? formatSpend(selectedValue) : '—',
    ...spendTone
  }, ...differenceRow] : hoverTarget === 'mostExpensive' ? [{
    label: mostExpensiveSegmentLabel ?? mostExpensiveLabel,
    value: mostExpensive != null ? formatSpend(mostExpensive.value) : '—',
    valueClassName: 'text-foreground'
  }, ...mostExpensiveComparisonRows] : [];
  const selectedFill = selectedValue != null && hasDomain ? positionForValue(selectedValue) : null;
  // Same blue fill as the AI Proficiency score power bar, rather than a cost tone.
  const fillColor = AIFI_CHART_PRIMARY_COLOR;
  return <div className={cn('relative', className)}>
      <div ref={barRef} className="relative" onMouseLeave={hideTooltip}>
        {hasDomain ? <div className={cn('absolute right-0 left-0 cursor-pointer transition-[filter] duration-150', barLit && 'brightness-110')} style={{
        top: barTopPx,
        height: `${layout.trackHeightPx}px`
      }} onMouseEnter={showBarTooltip}>
            <div className={cn('absolute inset-0 transition-[filter] duration-150', POWER_BAR_TRACK_CLASS, barLit && 'brightness-105')} />
            {!isDegenerate && selectedFill ? <div className={cn('absolute inset-y-0 left-0 transition-[filter] duration-150', barLit && 'brightness-105')} style={{
          width: selectedFill,
          ...powerBarColoredFillStyle(fillColor)
        }} /> : null}
          </div> : null}

        {orgAverage != null && hasDomain ? <PowerBarMarker layout={layout} position={positionForValue(orgAverage)} lit={averageLit} color={POWER_BAR_MEDIAN_TICK_COLOR} litColor={POWER_BAR_MEDIAN_TICK_COLOR_LIT} ariaLabel={averageLabel} onMouseEnter={showAverageTooltip} onBlur={hideTooltip} /> : null}

        {hasDomain ? <PowerBarMarker layout={layout} position={positionForValue(domainMax)} lit={mostExpensiveLit} color={SPEND_RANGE_MAX_TICK_COLOR} litColor={SPEND_RANGE_MAX_TICK_COLOR_LIT} ariaLabel={mostExpensiveMarkerLabel} onMouseEnter={showMostExpensiveTooltip} onFocus={showMostExpensiveTooltip} onBlur={hideTooltip} /> : null}

        <div style={{
        height: layout.markerHeightPx + 4
      }} />
      </div>

      {hasDomain ? <>
          <div className={cn('flex justify-between text-[10px] leading-none text-muted-foreground', PAGE_DATA_NUMBER_CLASS)}>
            <span>{formatSpend(cheapest.value)}</span>
            <span>{formatSpend(mostExpensive.value)}</span>
          </div>

          <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {orgAverage != null ? <RangeLegendItem color={POWER_BAR_MEDIAN_TICK_COLOR} label={averageLabel} tooltip={SPEND_RANGE_ORG_AVERAGE_TOOLTIP} /> : null}
            <RangeLegendItem color={SPEND_RANGE_MAX_TICK_COLOR} label={mostExpensiveLabel} tooltip={mostExpensiveTooltip(segmentNounPlural)} />
          </div>
        </> : null}

      <FloatingTooltip open={hoverTarget != null} x={tooltipPos.x} y={tooltipPos.y}>
        <ChartTooltipPanel rows={tooltipRows} />
      </FloatingTooltip>
    </div>;
}